import { decodeJwt } from 'jose';

const KEY = 'lexispulse-test:session:v1';
const PERSISTENT_KEY = 'lexispulse-test:session:v2';
export type StoredSession = { uid: string; token: string; persistent?: boolean; expiresAt?: number };

export function validStoredSession(value: unknown, now = Date.now()): value is StoredSession {
  if (!value || typeof value !== 'object') return false;
  const session = value as Partial<StoredSession>;
  if (typeof session.uid !== 'string' || typeof session.token !== 'string') return false;
  if (session.persistent) return session.token === '' && /^[a-zA-Z0-9_-]{4,128}$/.test(session.uid) &&
    typeof session.expiresAt === 'number' && Number.isFinite(session.expiresAt) && session.expiresAt > now + 30000;
  try {
    const claims = decodeJwt(session.token);
    return claims.sub === session.uid && typeof claims.exp === 'number' && claims.exp * 1000 > now + 30000;
  } catch { return false; }
}

export function clearStoredSession(): void {
  clearPersistentSession();
  try { sessionStorage.removeItem(KEY); } catch { /* Storage may be disabled. */ }
}

function clearPersistentSession(): void {
  try { localStorage.removeItem(PERSISTENT_KEY); } catch { return; }
}

function readPersistentSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(PERSISTENT_KEY);
    if (raw) {
      const metadata = JSON.parse(raw);
      const session = { uid: metadata.uid, token: '', persistent: true, expiresAt: metadata.expiresAt };
      if (validStoredSession(session)) return session;
      localStorage.removeItem(PERSISTENT_KEY);
    }
  } catch { clearPersistentSession(); }
  return null;
}

export function readStoredSession(): StoredSession | null {
  const persistent = readPersistentSession();
  if (persistent) return persistent;
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const session: unknown = JSON.parse(raw);
    if (validStoredSession(session)) return session;
  } catch { /* Invalid or inaccessible storage must not block sign-in. */ }
  clearStoredSession();
  return null;
}

export function saveStoredSession(session: StoredSession): void {
  clearStoredSession();
  if (!validStoredSession(session)) return;
  if (session.persistent) {
    try { localStorage.setItem(PERSISTENT_KEY, JSON.stringify({ uid: session.uid, expiresAt: session.expiresAt })); } catch { return; }
    return;
  }
  try { sessionStorage.setItem(KEY, JSON.stringify(session)); } catch { /* Sign-in works without persistence. */ }
}
